import React, { useCallback, useMemo, useState } from 'react';
import { Lock, Network, RefreshCw } from 'lucide-react';
import { OrgChartEditContext } from '../components/people/org/OrgChartEditContext';
import OrgChartBoard from '../components/people/org/OrgChartBoard';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canEditOrgChart } from '../utils/purchasingAccess';
import {
  crmUsersToOrgPeople,
  isOrgChartBot,
  ORG_DEPARTMENT_LABELS,
  ORG_LEVEL_LABELS,
  ORG_SEAT_LABELS,
  orgSeatOf,
  pinnedBotReason,
  pinnedSeatReason,
  resolveOrgPlacement,
} from '../utils/orgChart';
import {
  applyEffectiveReporting,
  defaultManagerFor,
  managerOptionsFor,
  reportingSyncDiff,
} from '../utils/orgReporting';
import { applyReportingRules, setOrgPlacement, setReportsToUserId } from '../utils/orgChartFirestore';
import { buildOrgChartLayout, ORG_BOT_CASTE_COLOR, ORG_GROUP_COLORS, ORG_POOL_COLOR } from '../utils/orgChartLayout';
import type { OrgChartPerson, OrgDepartment, OrgLevel } from '../types/purchasing';
import { ORG_DEPARTMENTS } from '../types/purchasing';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './PeopleHubPage.css';
import './PeopleOrgChartPage.css';

const STAFF_LEVELS: OrgLevel[] = ['managerial', 'general'];

const PeopleOrgChartPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [savingUid, setSavingUid] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const me = crmUsers.find(u => u.uid === user?.id);
  const canEdit = canEditOrgChart({
    authRole: user?.role,
    designations: me?.designations || user?.designations,
    briefingProfile: me?.executiveBriefing?.profile,
  });

  const people = useMemo(() => applyEffectiveReporting(crmUsersToOrgPeople(crmUsers)), [crmUsers]);
  const personById = useMemo(() => new Map(people.map(p => [p.uid, p])), [people]);
  const syncDiff = useMemo(() => reportingSyncDiff(people), [people]);
  const outOfSyncUids = useMemo(() => new Set(syncDiff.map(r => r.uid)), [syncDiff]);
  const graph = useMemo(() => buildOrgChartLayout(people), [people]);
  const selected = selectedUid ? personById.get(selectedUid) || null : null;
  const managerOptions = useMemo(
    () => (selected ? managerOptionsFor(selected, people) : []),
    [selected, people],
  );

  const persist = useCallback(async (uid: string, work: () => Promise<void>) => {
    setError(null);
    setNotice(null);
    setSavingUid(uid);
    try {
      await work();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update the org chart.');
    } finally {
      setSavingUid(null);
    }
  }, []);

  const onChangeManager = useCallback((person: OrgChartPerson, nextManagerId: string) => {
    void persist(person.uid, () => setReportsToUserId(person, nextManagerId || null, people));
  }, [people, persist]);

  const onChangePlacement = useCallback((
    person: OrgChartPerson,
    next: { orgLevel: OrgLevel; orgDepartment: OrgDepartment },
  ) => {
    void persist(person.uid, () => setOrgPlacement(person, next, people));
  }, [people, persist]);

  const onApplyRules = useCallback(async () => {
    if (syncDiff.length === 0) return;
    setError(null);
    setNotice(null);
    setApplying(true);
    try {
      const n = await applyReportingRules(syncDiff);
      setNotice(`Updated ${n} reporting line${n === 1 ? '' : 's'}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not apply the reporting rules.');
    } finally {
      setApplying(false);
    }
  }, [syncDiff]);

  const dropOnCell = useCallback((level: OrgLevel, department: OrgDepartment, uid: string) => {
    const person = personById.get(uid);
    if (!person) return;
    onChangePlacement(person, { orgLevel: level, orgDepartment: department });
    setSelectedUid(uid);
  }, [onChangePlacement, personById]);

  const dropOnPerson = useCallback((target: OrgChartPerson, uid: string) => {
    const person = personById.get(uid);
    if (!person || person.uid === target.uid) return;
    const place = resolveOrgPlacement(target);
    const nextPlace = { orgLevel: 'general' as OrgLevel, orgDepartment: place.orgDepartment };
    void persist(person.uid, async () => {
      await setOrgPlacement(person, nextPlace, people);
      await setReportsToUserId({ ...person, ...nextPlace }, target.uid, people);
    });
    setSelectedUid(uid);
  }, [people, persist, personById]);

  const selectedSeat = selected ? orgSeatOf(selected) : 'staff';
  const selectedPinned = !!selected && (
    selected.pinned === true || selectedSeat !== 'staff' || isOrgChartBot(selected)
  );
  const selectedPlace = selected ? resolveOrgPlacement(selected) : null;
  const selectedManager = selected?.effectiveReportsToUserId
    ? personById.get(selected.effectiveReportsToUserId) || null
    : null;
  const selectedDefault = selected && !selectedPinned && selectedPlace?.orgLevel === 'general'
    ? defaultManagerFor(selected, people)
    : null;
  const selectedExplicit = selected && !selectedPinned
    && selected.reportsToUserId
    && managerOptions.some(o => o.uid === selected.reportsToUserId)
    ? selected.reportsToUserId
    : '';

  return (
    <div className="people-hub-route-root people-org-route">
      <div className="ops-hub-toolbar">
        <div className="ops-hub-toolbar-left">
          <div>
            <h2 className="ops-hub-title people-org-title">
              <Network size={20} aria-hidden className="people-org-title-icon" />
              Org chart
            </h2>
            <p className="people-hub-subtitle people-org-subtitle">
              {canEdit
                ? 'Drag a staffer onto a Managerial or Staff cell to place them, or onto a manager in their group. Executives, the Office Manager, the 1099 pool, and CRM bots are pinned.'
                : 'Scroll to pan, pinch to zoom. Reporting lines drive Purchasing approvals.'}
            </p>
          </div>
        </div>
        <div className="people-org-toolbar-right">
          <ul className="people-org-legend" aria-label="Legend">
            {ORG_DEPARTMENTS.map(group => (
              <li key={group}>
                <span className="people-org-legend-swatch" style={{ background: ORG_GROUP_COLORS[group] }} />
                {ORG_DEPARTMENT_LABELS[group]}
              </li>
            ))}
            <li>
              <span className="people-org-legend-swatch is-dashed" style={{ borderColor: ORG_POOL_COLOR }} />
              1099 pool → COO
            </li>
            <li>
              <span className="people-org-legend-swatch" style={{ background: ORG_BOT_CASTE_COLOR }} />
              Bot Caste → CEO
            </li>
            <li>
              <Lock size={11} aria-hidden />
              Pinned seat
            </li>
          </ul>
          {canEdit && syncDiff.length > 0 && (
            <button
              type="button"
              className="pm-btn pm-btn-primary people-org-apply"
              disabled={applying}
              onClick={() => void onApplyRules()}
              title={syncDiff.map(r => r.displayName).join(', ')}
            >
              <RefreshCw size={14} aria-hidden className={applying ? 'is-spinning' : ''} />
              {applying ? 'Applying…' : `Apply reporting rules (${syncDiff.length})`}
            </button>
          )}
        </div>
      </div>
      {error && <p className="people-org-error">{error}</p>}
      {notice && !error && <p className="people-org-notice">{notice}</p>}
      <div className="people-org-stage">
        {people.length === 0 ? (
          <p className="people-org-empty">No active employees to show yet.</p>
        ) : (
          <OrgChartEditContext.Provider
            value={{
              canEdit,
              savingUid,
              managerOptions,
              outOfSyncUids,
              syncDiff,
              onChangeManager,
              onChangePlacement,
              onApplyRules: () => void onApplyRules(),
            }}
          >
            <OrgChartBoard
              graph={graph}
              canEdit={canEdit}
              selectedUid={selectedUid}
              onSelect={setSelectedUid}
              onDropCell={dropOnCell}
              onDropPerson={dropOnPerson}
            />
            {canEdit && selected && selectedPlace && (
              <div className="people-org-dock" onClick={e => e.stopPropagation()}>
                <div className="people-org-dock-who">
                  <strong>{selected.displayName}</strong>
                  <span className="people-org-dock-sub">
                    {ORG_SEAT_LABELS[selectedSeat] ? `${ORG_SEAT_LABELS[selectedSeat]} · ` : ''}
                    {selectedSeat === 'contractor'
                      ? '1099 contractor pool'
                      : isOrgChartBot(selected)
                        ? 'Bot Caste'
                        : `${ORG_LEVEL_LABELS[selectedPlace.orgLevel]} / ${ORG_DEPARTMENT_LABELS[selectedPlace.orgDepartment]}`}
                  </span>
                </div>
                {selectedPinned ? (
                  <div className="people-org-dock-pinned">
                    <Lock size={12} aria-hidden />
                    <span>
                      Reports to{' '}
                      <strong>{selectedManager ? selectedManager.displayName : 'nobody (apex)'}</strong>
                      {selectedManager && ORG_SEAT_LABELS[orgSeatOf(selectedManager)]
                        ? ` (${ORG_SEAT_LABELS[orgSeatOf(selectedManager)]})`
                        : ''}
                      {' · '}
                      {isOrgChartBot(selected) ? pinnedBotReason() : pinnedSeatReason(selectedSeat)}
                    </span>
                  </div>
                ) : (
                  <>
                    <label>
                      <span>Group</span>
                      <select
                        value={selectedPlace.orgDepartment}
                        disabled={savingUid === selected.uid}
                        onChange={e => onChangePlacement(selected, {
                          orgLevel: selectedPlace.orgLevel,
                          orgDepartment: e.target.value as OrgDepartment,
                        })}
                      >
                        {ORG_DEPARTMENTS.map(dept => (
                          <option key={dept} value={dept}>{ORG_DEPARTMENT_LABELS[dept]}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Layer</span>
                      <select
                        value={selectedPlace.orgLevel}
                        disabled={savingUid === selected.uid}
                        onChange={e => onChangePlacement(selected, {
                          orgLevel: e.target.value as OrgLevel,
                          orgDepartment: selectedPlace.orgDepartment,
                        })}
                      >
                        {STAFF_LEVELS.map(level => (
                          <option key={level} value={level}>{ORG_LEVEL_LABELS[level]}</option>
                        ))}
                      </select>
                    </label>
                    {selectedPlace.orgLevel === 'general' ? (
                      <label>
                        <span>Reports to</span>
                        <select
                          value={selectedExplicit}
                          disabled={savingUid === selected.uid}
                          onChange={e => onChangeManager(selected, e.target.value)}
                        >
                          <option value="">
                            {selectedDefault ? `Default — ${selectedDefault.displayName}` : 'Default'}
                          </option>
                          {managerOptions.map(o => (
                            <option key={o.uid} value={o.uid}>
                              {o.displayName}{ORG_SEAT_LABELS[orgSeatOf(o)] ? ` (${ORG_SEAT_LABELS[orgSeatOf(o)]})` : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <div className="people-org-dock-pinned">
                        <span>
                          Reports to{' '}
                          <strong>{selectedManager ? selectedManager.displayName : '—'}</strong>
                          {' · '}Managerial staff report to their group head.
                        </span>
                      </div>
                    )}
                  </>
                )}
                {outOfSyncUids.has(selected.uid) && (
                  <span className="people-org-dock-sync">
                    Stored manager differs from the rules — use Apply reporting rules.
                  </span>
                )}
              </div>
            )}
          </OrgChartEditContext.Provider>
        )}
      </div>
    </div>
  );
};

export default PeopleOrgChartPage;
